'use client';

import { useEffect, type ReactNode } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth-context';
import { trpc } from '@/lib/trpc';
import { useEntitlements } from '@/lib/use-entitlements';
import { AddPhoneGate } from './_components/AddPhoneGate';
import { SideNav } from './_components/SideNav';
import { TopBar } from './_components/TopBar';
import styles from './dashboard.module.css';

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const auth = useAuth();
  const logoutMutation = trpc.auth.logout.useMutation();

  // Auth guard: redirect unauthenticated users to /login.
  // We check both in-memory user and localStorage so that after a hard refresh
  // (user exists in storage but accessToken is null until first tRPC call)
  // we don't incorrectly redirect.
  useEffect(() => {
    const hasStoredSession =
      typeof window !== 'undefined' && localStorage.getItem('slikerr_session') !== null;
    if (!auth.user && !hasStoredSession) {
      router.replace('/login');
    }
  }, [auth.user, router]);

  const handleLogout = async () => {
    const refreshToken = auth.getRefreshToken();
    if (refreshToken) {
      try {
        await logoutMutation.mutateAsync({ refreshToken });
      } catch {
        // Proceed with client-side logout even if the server call fails
      }
    }
    auth.logout();
    router.replace('/login');
  };

  const businessName = auth.activeBusiness?.businessName ?? 'slots';
  const { entitlements } = useEntitlements();

  // Decision #29: an owner/staff account with no verified phone must be held
  // here, before ANY dashboard route renders — not just the landing page.
  // This lives at the layout boundary specifically so it holds by
  // construction: `DashboardLayout` wraps every route under `dashboard/*`,
  // so a bookmarked deep link re-mounts this same check instead of walking
  // past it. `enabled: !!auth.user` avoids firing this protectedProcedure
  // before the auth guard effect above has even decided whether to redirect.
  //
  // Fails OPEN while the query is loading/erroring (renders the normal shell
  // below) rather than blocking on an unresolved answer — the opposite
  // direction from `useEntitlements`' fail-safe, and deliberately so: this
  // only ever blocks once `hasVerifiedPhone` is POSITIVELY known false, so a
  // verified owner never sees the gate flash on a hard reload while the
  // query is still in flight.
  const profileQuery = trpc.auth.getProfile.useQuery(undefined, {
    enabled: !!auth.user,
    retry: 1,
  });
  if (auth.user && profileQuery.data?.hasVerifiedPhone === false) {
    return <AddPhoneGate onSignOut={handleLogout} />;
  }

  return (
    // flex row: in RTL the first child (SideNav) is on the inline-end (right)
    <div className={styles.shell}>
      <SideNav
        pathname={pathname ?? '/dashboard'}
        businessName={businessName}
        entitlements={entitlements}
      />
      <div className={styles.mainColumn}>
        <TopBar pathname={pathname ?? '/dashboard'} onLogout={handleLogout} />
        <main className={styles.main}>{children}</main>
      </div>
    </div>
  );
}
